/**
 * Normalize saved_schedule_items into the /heute agenda list.
 * Mail and flyer rows live on that table; Heute does not read extractions.
 */

import { isEventActionType, isTaskActionType } from "./schoolAttention.js";
import { parseVisibility } from "./scheduleVisibility.js";

/** @typedef {'mail'|'capture'|'manual'} ScheduleOrigin */
/** @typedef {'saved'} ScheduleKind */

export function originFromSavedItem(item) {
  if (item?.origin === "capture" || item?.sourceKind === "capture") return "capture";
  if (item?.origin === "mail" || item?.sourceKind === "mail") return "mail";
  return "manual";
}

export function isMailScheduleItem(item) {
  return originFromSavedItem(item) === "mail";
}

/**
 * @param {object} params
 * @returns {object}
 */
export function normalizeHeuteScheduleItem({
  kind = "saved",
  origin,
  actionType,
  title,
  date,
  end_date = null,
  time = null,
  all_day = true,
  place = null,
  child = null,
  overdue = false,
  remindEnabled = false,
  extractionId = null,
  eventIndex = null,
  rawEvent = null,
  corrections = null,
  id = null,
  visibility = "mine",
  householdId = null,
  sourceKind = null,
  sourceId = null,
  sourceIndex = null,
  isOwn = true,
  match_key = null,
  mailSubject = "",
}) {
  const isTask = isTaskActionType(actionType);
  const isEvent = isEventActionType(actionType);
  const key = id ? `saved-${id}` : `mail-${extractionId}-${eventIndex}`;

  return {
    key,
    kind: kind || "saved",
    origin,
    actionType: isTask ? "task" : isEvent ? "event" : String(actionType || "event"),
    isTask,
    isEvent,
    title: title || "(ohne Titel)",
    date: date || "",
    end_date: end_date || null,
    time,
    all_day,
    place,
    child,
    overdue: !!overdue,
    remindEnabled: !!remindEnabled,
    extractionId,
    eventIndex,
    rawEvent,
    corrections,
    id,
    visibility: parseVisibility(visibility),
    householdId,
    sourceKind: sourceKind || origin,
    sourceId,
    sourceIndex,
    isOwn,
    match_key: match_key || null,
    mailSubject: origin === "mail" ? String(mailSubject || "").replace(/\s+/g, " ").trim() : "",
  };
}

function savedItemToHeute(item) {
  const origin = originFromSavedItem(item);
  const sourceId = item.sourceId || item.mailExtractionId || item.captureId || null;
  const sourceIndex = Number.isInteger(item.sourceIndex) ? item.sourceIndex : null;
  const extractionId =
    origin === "mail" ? item.mailExtractionId || sourceId : null;
  return normalizeHeuteScheduleItem({
    kind: "saved",
    origin,
    actionType: item.action_type,
    title: item.title,
    date: item.date,
    end_date: item.end_date || null,
    time: item.time,
    all_day: item.all_day,
    place: item.place,
    child: item.child || null,
    overdue: item.overdue,
    remindEnabled: item.remindEnabled,
    extractionId,
    eventIndex: origin === "mail" ? sourceIndex : null,
    id: item.id,
    visibility: item.visibility,
    householdId: item.householdId || null,
    sourceKind: item.sourceKind || origin,
    sourceId,
    sourceIndex,
    isOwn: item.isOwn !== false,
    mailSubject: origin === "mail" ? item.mailSubject || "" : "",
    rawEvent: {
      title: item.title,
      date: item.date,
      end_date: item.end_date || null,
      time: item.time,
      all_day: item.all_day,
      action_type: item.action_type,
      child: item.child || null,
      applies_to: item.applies_to || [],
      event_kind: item.event_kind || null,
      source_quote: item.source_quote || null,
      match_key: item.match_key || null,
    },
    match_key: item.match_key || null,
  });
}

/**
 * @param {object[]} savedItems
 */
export function mergeHeuteScheduleItems(savedItems) {
  const items = (savedItems || []).map(savedItemToHeute);

  items.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    if (a.date !== b.date) return String(a.date).localeCompare(String(b.date));
    return String(a.time || "").localeCompare(String(b.time || ""));
  });

  return items;
}

/**
 * @param {object[]} items
 * @param {'all'|'task'|'event'} filter
 */
export function filterHeuteScheduleItems(items, filter) {
  if (filter === "task") return items.filter((i) => i.isTask);
  if (filter === "event") return items.filter((i) => i.isEvent);
  return items;
}

/**
 * @param {object[]} items
 */
export function countHeuteScheduleSummary(items) {
  let overdue = 0;
  let tasks = 0;
  let events = 0;
  for (const item of items) {
    if (item.overdue) overdue += 1;
    if (item.isTask) tasks += 1;
    if (item.isEvent) events += 1;
  }
  return { overdue, tasks, events, total: items.length };
}

function isSameSavedRow(row, item) {
  if (item.id && row?.id === item.id) return true;
  if (item.extractionId && Number.isInteger(item.eventIndex)) {
    return row?.sourceId === item.extractionId && row?.sourceIndex === item.eventIndex;
  }
  return false;
}

/**
 * Drop a done item from the /heute saved list (optimistic UI).
 */
export function omitDoneScheduleSources(sources, item) {
  const savedItems = sources.savedItems || [];
  return {
    savedItems: savedItems.filter((row) => !isSameSavedRow(row, item)),
  };
}

/**
 * Put a mistakenly marked-done saved row back on /heute (optimistic UI).
 */
export function restoreDoneScheduleSources(sources, row) {
  const savedItems = sources.savedItems || [];
  if (!row?.id) return { savedItems };
  if (savedItems.some((existing) => existing.id === row.id)) {
    return { savedItems };
  }
  return { savedItems: [...savedItems, row] };
}

const SCHEDULE_FIELD_KEYS = ["title", "date", "end_date", "time", "all_day", "place", "child", "visibility"];

/**
 * Copy edited fields onto a saved row or an open agenda item.
 * @param {object} row
 * @param {object} patch
 */
export function applyScheduleBody(row, patch) {
  const next = { ...row };
  for (const key of SCHEDULE_FIELD_KEYS) {
    if (patch && Object.prototype.hasOwnProperty.call(patch, key)) next[key] = patch[key];
  }
  if (Object.prototype.hasOwnProperty.call(patch || {}, "remind")) {
    next.remind = !!patch.remind;
    next.remindEnabled = !!patch.remind;
  }
  return next;
}

/**
 * The open popup item, including the calendar fields copied from the raw event.
 * @param {object|null|undefined} item
 * @param {object} patch
 */
export function applyScheduleFieldPatch(item, patch) {
  if (!item) return item;
  const next = applyScheduleBody(item, patch);
  if (item.rawEvent) next.rawEvent = applyScheduleBody(item.rawEvent, patch);
  return next;
}

/**
 * Paint a field edit onto the saved rows before the request returns.
 * @param {{ savedItems?: object[] }} sources
 * @param {object} item
 * @param {object} patch
 */
export function patchScheduleFieldSources(sources, item, patch) {
  return {
    savedItems: (sources.savedItems || []).map((row) =>
      isSameSavedRow(row, item) ? applyScheduleBody(row, patch) : row,
    ),
  };
}

/**
 * Toggle remind on the matching saved row without a full refetch.
 */
export function patchRemindScheduleSources(sources, item, remindEnabled) {
  const nextRemind = !!remindEnabled;
  const patchSaved = (row) =>
    isSameSavedRow(row, item)
      ? { ...row, remindEnabled: nextRemind, remind: nextRemind }
      : row;
  return {
    savedItems: (sources.savedItems || []).map(patchSaved),
  };
}
